import React, { useEffect, useState } from 'react';
import { fetchAuditLog } from '../../services/api/adminAuditLog';

// Status colour uses the page's semantic tokens: `completed_with_errors` is a
// success that needs attention, so it is not painted the same as `failed`.
const PURGE_STATUS_CLASS = {
  completed: 'bg-muted text-foreground',
  completed_with_errors: 'bg-destructive/10 text-destructive',
  failed: 'bg-destructive/10 text-destructive',
};

// deleted_counts is an open-ended {table => n} map, so it is rendered generically
// rather than against a fixed list of tables.
const CountList = ({ counts }) => {
  const entries = Object.entries(counts || {}).filter(([, n]) => n);
  if (!entries.length) return <span className="text-muted-foreground">nothing deleted</span>;
  return (
    <span className="text-muted-foreground">
      {entries.map(([table, n]) => `${table} ${n}`).join(', ')}
    </span>
  );
};

// One table, three actions — and they are not interchangeable: a purge destroys
// the account, an archive freezes it, a restore reverses the archive. Rendering
// all three through a hardcoded "Deleted: ..." line reported an archive as
// "Deleted: clients 1", in the admin's only durable record, for a feature whose
// entire promise is that nothing is deleted and restore returns the account as
// it was. The verb comes from entry.action; the counts line is purge-only.
//
// Unknown/absent action falls back to purge — the only action that existed
// before archive and restore, so that is what an old row without one is.
const ACCOUNT_ACTIONS = {
  purge: { verb: 'Purged', countsLabel: 'Deleted' },
  archive: { verb: 'Archived', countsLabel: null },
  restore: { verb: 'Restored', countsLabel: null },
  workspace_archive: { verb: 'Archived workspace', countsLabel: null },
  workspace_restore: { verb: 'Restored workspace', countsLabel: null },
};

// The purge modal tells admins to "check the purge audit log", and this row is
// the only surviving trace of the account — so every recorded field is shown,
// including the ones that record partial failure.
const AccountActionEntry = ({ entry }) => {
  const workspaces = entry.workspace_names || [];
  const retained = entry.retained_user_ids || [];
  const rehomed = entry.rehomed || [];
  const externalErrors = entry.external_errors || [];
  const action = ACCOUNT_ACTIONS[entry.action] || ACCOUNT_ACTIONS.purge;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-foreground">{action.verb}</span>
        <span className="font-medium text-foreground">{entry.owner_email || 'unknown owner'}</span>
        {entry.owner_name && <span className="text-muted-foreground">({entry.owner_name})</span>}
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            PURGE_STATUS_CLASS[entry.status] || 'bg-muted text-muted-foreground'
          }`}
        >
          {entry.status}
        </span>
        <span className="text-xs text-muted-foreground">account #{entry.billing_account_id}</span>
      </div>

      <div className="text-muted-foreground">
        Workspaces: {workspaces.length ? workspaces.join(', ') : '—'}
      </div>

      {/* Archive and restore write a counts map too ({"clients" => n}), but it
          is a tally of what was FROZEN or FREED, not deleted — so the line is
          purge-only rather than relabelled. The workspaces line above already
          names exactly what an archive or restore touched. */}
      {action.countsLabel && (
        <div>
          {action.countsLabel}: <CountList counts={entry.deleted_counts} />
        </div>
      )}

      {retained.length > 0 && (
        <div className="text-muted-foreground">
          Retained users (kept their login): {retained.join(', ')}
        </div>
      )}

      {rehomed.length > 0 && (
        <div className="text-muted-foreground">
          Rehomed:{' '}
          {rehomed
            .map((r) => `${r.table} #${r.id} → workspace ${r.to_client_id}`)
            .join(', ')}
        </div>
      )}

      {externalErrors.length > 0 && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1 text-destructive">
          <div className="font-medium">External effects that failed:</div>
          <ul className="list-disc pl-5">
            {externalErrors.map((x, i) => (
              <li key={`${x.effect}-${i}`}>
                <span className="font-mono text-xs">{x.effect}</span>
                {x.error ? ` — ${x.error}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {entry.error_message && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1 text-destructive">
          {entry.error_message}
        </div>
      )}
    </div>
  );
};

const AdminAuditLog = () => {
  const [type, setType] = useState('feature_override');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchAuditLog({ type });
        setEntries(res.data?.entries || []);
      } catch (e) {
        setError(e?.response?.data?.error || e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [type]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground mb-6">Audit Log</h1>
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm text-foreground">Type:</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="text-sm border border-input rounded-lg px-3 py-2 focus:ring-black focus:border-black"
        >
          <option value="feature_override">Feature overrides</option>
          <option value="role_permission">Role permissions</option>
          <option value="role">Role create/update/delete</option>
          <option value="account_purge">Account purges, archives &amp; restores</option>
        </select>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading...</div>
      ) : (
        <div className="bg-card rounded-lg border border-border">
          <div className="divide-y divide-gray-100">
            {entries.map((e) => (
              <div key={`${e.type}-${e.id}`} className="px-4 py-3 text-sm">
                <div className="flex items-baseline justify-between">
                  <div className="flex-1">
                    {type === 'feature_override' && (
                      <span>
                        <span className="font-medium text-foreground">{e.action}</span>
                        {' '}sub_feature {e.sub_feature_id} on workspace {e.client_id}
                        {e.reason && <> &mdash; <span className="text-muted-foreground">"{e.reason}"</span></>}
                      </span>
                    )}
                    {type === 'role_permission' && (
                      <span>
                        Role {e.rbac_role_id}, component {e.feature_component_id}:{' '}
                        R={e.prior_can_read ? '✓' : '·'}/{e.new_can_read ? '✓' : '·'}{' '}
                        W={e.prior_can_write ? '✓' : '·'}/{e.new_can_write ? '✓' : '·'}
                      </span>
                    )}
                    {type === 'role' && (
                      <span>
                        <span className="font-medium text-foreground">{e.action}</span> role {e.rbac_role_id}
                      </span>
                    )}
                    {type === 'account_purge' && <AccountActionEntry entry={e} />}
                  </div>
                  <div className="text-xs text-muted-foreground flex-shrink-0 ml-4 text-right">
                    <div>
                      {e.performed_by_email || 'system'} &middot; {new Date(e.created_at).toLocaleString()}
                    </div>
                    {/* These are the one audit here that spans time, so the
                        request, start and finish are all worth showing — a purge
                        that began three seconds ago reads very differently from
                        one that has been running for three hours. */}
                    {type === 'account_purge' && (e.started_at || e.finished_at) && (
                      <div>
                        {e.started_at && <>started {new Date(e.started_at).toLocaleString()}</>}
                        {e.started_at && e.finished_at && ' · '}
                        {e.finished_at && <>finished {new Date(e.finished_at).toLocaleString()}</>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {entries.length === 0 && (
              <div className="px-4 py-4 text-center text-sm text-muted-foreground">No audit entries.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuditLog;
